import React, {ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {computeMultiSelectCheckboxState} from '../../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../../components/common/table/staticVirtualTable';
import {TableEmptyState} from '../../../components/common/table/tableEmptyState';
import {TableMultiSelectHeaderRow} from '../../../components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {VirtualTableRowSiblingData} from '../../../components/common/table/virtualTableBase';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {SETTINGS_SIDEBAR_WIDTH} from '../../common/settingsWrapper';
import {settingsHorizontalPaddingStyles} from '../../settingsCommonConstants';
import {ResourceTableActions} from './resourceTableActions';
import {ResourceTableRow} from './resourceTableRow';
import {ResourceColumn} from './resourceTableTypes';

/*
 * Props.
 */

interface ResourceTableProps<T extends {id: unknown}> {
  isSidebarDisplayed: boolean;

  tableColumns: ReadonlyArray<ResourceColumn<T>>;
  renderEmptyText: (hasError: boolean, hasSearchQuery: boolean) => ReactNode;

  items: ReadonlyArray<T>;
  onLoadMore: Handler;
  moreStatus: AsyncMoreStatusesEnum;
  hasError: boolean;
  hasSearchQuery: boolean;

  selectedIds: ReadonlyArray<T['id']>;

  toggleIds: (shouldSelect: boolean, ids: ReadonlyArray<T['id']>) => void;
  onDelete?: Handler;

  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: T['id']) => void;

  renderSelectedItemsLabel: (count: number) => ReactNode;
  renderDeleteItemsTitle: (count: number, item: T | undefined) => ReactNode;
  renderDeleteItemsDescription: (count: number) => ReactNode;
}

/*
 * Styles.
 */

const StyledTableEmptyState = styled(TableEmptyState)<{$isSidebarDisplayed: boolean}>`
  ${settingsHorizontalPaddingStyles};

  ${(p) =>
    p.$isSidebarDisplayed &&
    css`
      margin-right: ${SETTINGS_SIDEBAR_WIDTH}px;
    `}
`;

/*
 * Component.
 */

export const ResourceTable = <T extends {id: unknown}>(props: ResourceTableProps<T>) => {
  const {
    isSidebarDisplayed,
    tableColumns,
    renderEmptyText,
    items,
    onLoadMore,
    moreStatus,
    hasError,
    hasSearchQuery,
    selectedIds,
    toggleIds,
    onDelete,
    navigateTo,
    renderSelectedItemsLabel,
    renderDeleteItemsTitle,
    renderDeleteItemsDescription,
  } = props;

  const [lastSelectedId, setLastSelectedId] = useState<T['id']>();

  const renderRow = (
    item: T,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<T>,
  ) => {
    const onSelect = () => navigateTo(SettingsResourcePagesEnum.EDIT, item.id);

    return (
      <ResourceTableRow
        item={item}
        options={options}
        tableColumns={tableColumns}
        isSelected={selectedIds.includes(item.id)}
        isInMultiSelectMode={selectedIds.length > 0}
        isPreviousSiblingSelected={selectedIds.includes(siblingData?.previous?.id ?? -1)}
        isNextSiblingSelected={selectedIds.includes(siblingData?.next?.id ?? -1)}
        onSelect={onSelect}
        onMultiSelect={() => {
          toggleIds(true, findSelectedIds(item, lastSelectedId, items));
        }}
        onCheckBoxToggle={(shouldSelect) => {
          toggleIds(shouldSelect, [item.id]);

          if (shouldSelect) {
            setLastSelectedId(item.id);
          }
        }}
      />
    );
  };

  const renderMultiSelectHeader = (onMultiSelectChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      multiSelectStatus={computeMultiSelectCheckboxState(items.length, selectedIds.length)}
      renderTotalItemsLabel={() => renderSelectedItemsLabel(selectedIds.length)}
      onMultiSelectChange={onMultiSelectChange}
    >
      <ResourceTableActions
        items={items}
        selectedIds={selectedIds}
        onDelete={onDelete}
        renderDeleteItemsTitle={renderDeleteItemsTitle}
        renderDeleteItemsDescription={renderDeleteItemsDescription}
      />
    </TableMultiSelectHeaderRow>
  );

  const shouldRenderEmtpyState =
    hasError || (moreStatus === AsyncMoreStatusesEnum.ALL_LOADED && items.length === 0);

  const renderEmptyState = () => {
    // Settings header (122px) + Settings content header (30px) + Table header (48px) + margin (50px);
    const headerOffset = 122 + 30 + 48 + 50;

    return (
      <StyledTableEmptyState $isSidebarDisplayed={isSidebarDisplayed} headerOffset={headerOffset}>
        {renderEmptyText(hasError, hasSearchQuery)}
      </StyledTableEmptyState>
    );
  };

  return (
    <StaticVirtualTable<T>
      items={items}
      columns={tableColumns}
      hasData={moreStatus !== AsyncMoreStatusesEnum.NONE}
      moreStatuses={{
        [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
        // Set `ALL_LOADED` to hide loading rows if something went wrong.
        [ScrollDirectionsEnum.BOTTOM]: hasError ? AsyncMoreStatusesEnum.ALL_LOADED : moreStatus,
      }}
      onLoadMore={onLoadMore}
      renderRow={renderRow}
      shouldRenderEmptyState={shouldRenderEmtpyState}
      renderEmpty={renderEmptyState}
      shouldAllowMultiSelect
      isInMultiSelectMode={selectedIds.length > 0}
      renderMultiSelectHeader={renderMultiSelectHeader}
      onMultiSelectHeaderChange={(isSelectingAll) => {
        const ids = items.map((i) => i.id);
        toggleIds(isSelectingAll, ids);
      }}
    />
  );
};

/*
 * Helpers.
 */

function findSelectedIds<T extends {id: unknown}>(
  currentItem: T,
  lastSelectedId: T['id'] | undefined,
  items: ReadonlyArray<T>,
): ReadonlyArray<T['id']> {
  if (!lastSelectedId) {
    return [];
  }

  const ids = items.map((i) => i.id);
  const currentIndex = ids.indexOf(currentItem.id);
  const previousIndex = ids.indexOf(lastSelectedId);

  if (currentIndex < previousIndex) {
    return ids.slice(currentIndex, previousIndex);
  }

  return ids.slice(previousIndex, currentIndex + 1);
}
